import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmailValid } from 'utils/validator';
import useClipboardValue from 'hooks/useClipboardValue';
import Keyboard from 'containers/Keyboard';
import { StyledKeyboardWrapper } from './FormStyledComponents';
import ActionsSection from './ActionsSection';

interface IEmailFrom {
  initialValue?: string;
  placeholder?: string;
  invalidMessage?: string;
  buttonLabel: string;
  sectionName?: string;
  onSubmit: (email: string) => void;
}

const EmailForm: React.FC<IEmailFrom> = (props) => {
  const {
    initialValue,
    placeholder,
    invalidMessage,
    buttonLabel,
    sectionName,
    onSubmit,
    children,
  } = props;

  const [email, setEmail] = useState(initialValue);
  const [cursorPosition, setCursorPosition] = useState((initialValue || '').length);
  const [isValid, setIsValid] = useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const showKeyboard = useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = useCallback(() => setIsKeyboardVisible(false), []);
  const onValueUpdate = useCallback((value) => setEmail(value), []);
  const onCursorPositionUpdate = useCallback((value) => setCursorPosition(value), []);
  const onButtonClick = useCallback(() => {
    if (isEmailValid(email)) {
      onSubmit(email);
    } else {
      setIsValid(false);
    }
  }, [email, onSubmit]);

  useClipboardValue((value) => {
    if (!isEmailValid(value)) return;
    setEmail(value);
    setCursorPosition(value.length);
    setIsKeyboardVisible(false);
  });

  return (
    <>
      <ActionsSection id="email-section" sectionName={sectionName} defaultSection>
        <ActionsSection.Input
          value={email}
          cursor={isKeyboardVisible}
          cursorPosition={cursorPosition}
          placeholder={placeholder}
          isValid={!isKeyboardVisible ? isValid : true}
          invalidMessage={invalidMessage}
          onEnter={showKeyboard}
        />
        <ActionsSection.Button label={buttonLabel} onEnter={onButtonClick} />

        {children}
      </ActionsSection>
      {isKeyboardVisible && (
        <StyledKeyboardWrapper>
          <Keyboard
            onQueryUpdated={onValueUpdate}
            onCursorPositionUpdate={onCursorPositionUpdate}
            onLeave={hideKeyboard}
            query={email}
            maxQuerySize={130}
            selectedLanguage="en"
            type="email"
          />
        </StyledKeyboardWrapper>
      )}
    </>
  );
};

EmailForm.defaultProps = {
  sectionName: '@EMAIL_INPUT',
  initialValue: '',
};

EmailForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  initialValue: PropTypes.string,
  placeholder: PropTypes.string,
  invalidMessage: PropTypes.string,
  sectionName: PropTypes.string,
};

export default EmailForm;
